import {
  Component,
  type ErrorInfo,
  type PropsWithChildren,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState
} from "react";
import type { ToolManifest } from "@local-tools/tool-sdk";

function ShieldIcon({ decorative = true }: { decorative?: boolean }) {
  return (
    <svg
      aria-hidden={decorative}
      className="ui-icon"
      fill="none"
      viewBox="0 0 24 24"
    >
      <path
        d="M12 3.2 19 6v5.4c0 4.3-2.6 7.8-7 9.6-4.4-1.8-7-5.3-7-9.6V6l7-2.8Z"
        stroke="currentColor"
        strokeLinejoin="round"
        strokeWidth="1.8"
      />
      <path
        d="m8.6 12.1 2.1 2.1 4.8-4.9"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.8"
      />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg aria-hidden="true" className="ui-icon" fill="none" viewBox="0 0 24 24">
      <circle cx="10.8" cy="10.8" r="6.7" stroke="currentColor" strokeWidth="1.8" />
      <path d="m16 16 4.2 4.2" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
    </svg>
  );
}

function SunIcon() {
  return (
    <svg aria-hidden="true" className="ui-icon" fill="none" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="3.8" stroke="currentColor" strokeWidth="1.8" />
      <path
        d="M12 2v2.2M12 19.8V22M4.9 4.9l1.5 1.5M17.6 17.6l1.5 1.5M2 12h2.2M19.8 12H22M4.9 19.1l1.5-1.5M17.6 6.4l1.5-1.5"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="1.8"
      />
    </svg>
  );
}

export function AppHeader({
  brand,
  tools,
  currentToolId,
  theme,
  onThemeToggle
}: {
  brand: string;
  tools: ToolManifest[];
  currentToolId: string;
  theme: "light" | "dark";
  onThemeToggle: () => void;
}) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const dialogTitleId = useId();

  const openSearch = useCallback(() => {
    previousFocusRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    setSearchOpen(true);
  }, []);

  const closeSearch = useCallback(() => {
    setSearchOpen(false);
    window.requestAnimationFrame(() => previousFocusRef.current?.focus());
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        openSearch();
      }
      if (event.key === "Escape" && searchOpen) {
        closeSearch();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [closeSearch, openSearch, searchOpen]);

  useEffect(() => {
    if (searchOpen) {
      inputRef.current?.focus();
    }
  }, [searchOpen]);

  const filteredTools = tools.filter((tool) => {
    const haystack = [
      tool.title,
      tool.description,
      tool.category,
      ...tool.keywords
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(query.trim().toLowerCase());
  });

  return (
    <>
      <header className="app-header">
        <div className="app-header__inner">
          <a aria-label={`${brand} home`} className="wordmark" href="/">
            <span aria-hidden="true" className="wordmark__mark">
              {brand.charAt(0).toLowerCase()}
            </span>
            <span>{brand}</span>
          </a>
          <div className="app-header__actions">
            <button
              aria-label="Find a tool"
              className="tool-search-trigger"
              onClick={openSearch}
              type="button"
            >
              <SearchIcon />
              <span>Find a tool</span>
              <kbd>⌘K</kbd>
            </button>
            <button
              aria-label={`Switch to ${theme === "light" ? "dark" : "light"} theme`}
              className="icon-button"
              onClick={onThemeToggle}
              type="button"
            >
              <SunIcon />
            </button>
          </div>
        </div>
      </header>
      {searchOpen ? (
        <div
          aria-labelledby={dialogTitleId}
          aria-modal="true"
          className="command-backdrop"
          onMouseDown={(event) => {
            if (event.currentTarget === event.target) {
              closeSearch();
            }
          }}
          onKeyDown={(event) => {
            if (event.key !== "Tab") {
              return;
            }
            const focusable = Array.from(
              event.currentTarget.querySelectorAll<HTMLElement>(
                'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
              )
            );
            const first = focusable[0];
            const last = focusable.at(-1);
            if (event.shiftKey && document.activeElement === first) {
              event.preventDefault();
              last?.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
              event.preventDefault();
              first?.focus();
            }
          }}
          role="dialog"
        >
          <div className="command-dialog">
            <div className="command-dialog__search">
              <SearchIcon />
              <label className="sr-only" htmlFor="tool-search">
                Find a tool
              </label>
              <input
                autoComplete="off"
                id="tool-search"
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search local tools"
                ref={inputRef}
                type="search"
                value={query}
              />
              <button
                aria-label="Close tool search"
                className="command-dialog__close"
                onClick={closeSearch}
                type="button"
              >
                Esc
              </button>
            </div>
            <h2 className="sr-only" id={dialogTitleId}>
              Tool search
            </h2>
            <div className="command-results">
              {filteredTools.length > 0 ? (
                filteredTools.map((tool) => (
                  <a
                    aria-current={tool.id === currentToolId ? "page" : undefined}
                    className="command-result"
                    href={tool.route}
                    key={tool.id}
                  >
                    <span>
                      <strong>{tool.title}</strong>
                      <small>{tool.description}</small>
                    </span>
                    <span className="command-result__status">
                      {tool.offline ? "Runs locally" : "Network tool"}
                    </span>
                  </a>
                ))
              ) : (
                <p className="command-empty">No tools match that search.</p>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

export function ToolBreadcrumb({
  category,
  title
}: {
  category: string;
  title: string;
}) {
  return (
    <nav aria-label="Breadcrumb" className="tool-breadcrumb">
      <span>{category}</span>
      <span aria-hidden="true">/</span>
      <span aria-current="page">{title}</span>
    </nav>
  );
}

export function ToolIntro({
  title,
  description,
  action
}: {
  title: string;
  description: string;
  action?: ReactNode;
}) {
  return (
    <section aria-labelledby="tool-title" className="tool-intro">
      <div className="tool-intro__heading">
        <div>
          <h1 id="tool-title">{title}</h1>
          <p>{description}</p>
        </div>
        {action ? <div className="tool-intro__action">{action}</div> : null}
      </div>
      <p className="tool-trust-line">
        <ShieldIcon />
        <strong>Nothing is uploaded or saved</strong>
      </p>
    </section>
  );
}

export function ToolWorkbench({ children }: PropsWithChildren) {
  return <section className="tool-workbench">{children}</section>;
}

export function InputCard({
  title,
  hint,
  children,
  footer
}: PropsWithChildren<{
  title: string;
  hint?: string;
  footer?: ReactNode;
}>) {
  return (
    <section aria-labelledby="input-card-title" className="input-card">
      <header className="input-card__header">
        <h2 id="input-card-title">{title}</h2>
        {hint ? <span>{hint}</span> : null}
      </header>
      {children}
      {footer ? <footer className="input-card__footer">{footer}</footer> : null}
    </section>
  );
}

export type NoticeTone = "info" | "warning" | "danger" | "critical" | "success";

export function Notice({
  title,
  children,
  tone = "info"
}: PropsWithChildren<{ title: string; tone?: NoticeTone }>) {
  return (
    <div className={`notice notice--${tone}`}>
      <span aria-hidden="true" className="notice__marker" />
      <div>
        <strong>{title}</strong>
        <div className="notice__body">{children}</div>
      </div>
    </div>
  );
}

export function FactGrid({
  items
}: {
  items: Array<{ label: string; value: ReactNode }>;
}) {
  return (
    <dl className="fact-grid">
      {items.map((item) => (
        <div className="fact-cell" key={item.label}>
          <dt>{item.label}</dt>
          <dd>{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function CodePanel({
  code,
  label
}: {
  code: string;
  label: string;
}) {
  return (
    <pre aria-label={label} className="code-panel" tabIndex={0}>
      <code>{code}</code>
    </pre>
  );
}

export function CopyButton({
  label,
  displayLabel,
  value,
  copiedLabel,
  onFeedback
}: {
  label: string;
  displayLabel?: string;
  value: string;
  copiedLabel: string;
  onFeedback: (message: string) => void;
}) {
  const [state, setState] = useState<"idle" | "copied" | "failed">("idle");
  const timeoutRef = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
      }
    },
    []
  );

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setState("copied");
      onFeedback(copiedLabel);
    } catch {
      setState("failed");
      onFeedback("Copy failed. Select the text and copy it manually.");
    }
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = window.setTimeout(() => setState("idle"), 1800);
  };

  return (
    <button
      aria-label={label}
      className="copy-button"
      onClick={copy}
      type="button"
    >
      {state === "copied"
        ? "Copied"
        : state === "failed"
          ? "Select manually"
          : (displayLabel ?? label)}
    </button>
  );
}

export function ToastRegion({ message }: { message: string }) {
  return (
    <div aria-atomic="true" aria-live="polite" className="toast-region">
      {message ? <div className="toast">{message}</div> : null}
    </div>
  );
}

export function EmptyResult({
  title,
  children,
  action,
  secondaryAction
}: PropsWithChildren<{
  title: string;
  action?: ReactNode;
  secondaryAction?: ReactNode;
}>) {
  return (
    <section className="empty-result">
      <div aria-hidden="true" className="empty-token-rail">
        <span />
        <span />
        <span />
      </div>
      <h2>{title}</h2>
      <div className="empty-result__copy">{children}</div>
      <div className="empty-result__actions">
        {action}
        {secondaryAction}
      </div>
    </section>
  );
}

export function ResultCard({
  title,
  actions,
  children,
  className = ""
}: PropsWithChildren<{
  title: string;
  actions?: ReactNode;
  className?: string;
}>) {
  return (
    <section className={`result-card ${className}`.trim()}>
      <header className="result-card__header">
        <h2>{title}</h2>
        {actions ? <div className="result-card__actions">{actions}</div> : null}
      </header>
      {children}
    </section>
  );
}

export class ToolErrorBoundary extends Component<
  PropsWithChildren,
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(_error: Error, _info: ErrorInfo) {
    // Production telemetry must never include tool input. Intentionally no-op.
  }

  render() {
    if (this.state.error) {
      return (
        <section className="tool-runtime-boundary" role="alert">
          <p className="eyebrow">Contained tool error</p>
          <h1>This tool stopped unexpectedly</h1>
          <p>
            The rest of the toolbox is still available. Reload this tool to
            start with a clean worker.
          </p>
          <button onClick={() => window.location.reload()} type="button">
            Reload tool
          </button>
        </section>
      );
    }
    return this.props.children;
  }
}
